// Custom. Ref: https://github.com/shadcn-ui/ui/issues/4732
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { motion } from 'motion/react';
import * as React from 'react';

import { cn } from '../../utils/cn';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('border-b border-dotted', className)}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const content = props?.asChild ? (
    <div className="no-underline hover:no-underline">{children}</div>
  ) : (
    <>
      {children}
      <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform duration-200" />
    </>
  );

  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          'flex flex-1 items-center justify-between py-4 text-left text-sm font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180',
          className,
        )}
        {...props}
      >
        {content}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
});
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
        return;
      }

      if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  React.useEffect(() => {
    const node = contentRef.current;

    if (!node) {
      return;
    }

    const syncState = () => {
      setIsOpen(node.getAttribute('data-state') === 'open');
    };

    syncState();

    const observer = new MutationObserver(syncState);
    observer.observe(node, { attributes: true, attributeFilter: ['data-state'] });

    return () => observer.disconnect();
  }, []);

  return (
    <AccordionPrimitive.Content
      ref={setRefs}
      className="overflow-hidden text-sm"
      forceMount
      {...props}
    >
      <motion.div
        animate={isOpen ? 'open' : 'closed'}
        initial={false}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        variants={{
          open: { height: 'auto', opacity: 1, y: 0 },
          closed: { height: 0, opacity: 0, y: -6 },
        }}
      >
        <div className={cn('pt-0 pb-4', className)}>{children}</div>
      </motion.div>
    </AccordionPrimitive.Content>
  );
});
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
